import { useCallback, useState } from "react";
import { getHeaders } from "$utils/utils";
import { ImportError, ImportType } from "$types/import/ImportType";
import { downloadFile } from "$components/Export/utils";

export type UseExportType = {
  importItems: (token: string, body: any, type: ImportType) => Promise<any>;
  analyzeImportItems: (
    token: string,
    body: any,
    type: ImportType
  ) => Promise<null | Response>;
  errors: ImportError[];
  setErrors: (errors: ImportError[]) => void;
  exportData: (
    token: string,
    body: any,
    url: string
  ) => Promise<null | Response>;
  exportXLS: (
    token: string,
    filters: any,
    type: string,
    fileName?: string
  ) => Promise<void>;
  exportXLSTemplate: (
    token: string,
    type: string,
    fileName?: string
  ) => Promise<void>;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
};

const useExport = (): UseExportType => {
  const apiUrl = process.env.REACT_APP_API_ENDPOINT;
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const analyzeImportItems = useCallback(
    async (token: string, body: any, type: ImportType) => {
      const headers = getHeaders(token);
      const response = await fetch(`${apiUrl}/api/import/analyze/${type}`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.status === 400) {
        setErrors(data.errors || []);
      }
      return data;
    },
    [apiUrl]
  );

  const importItems = async (
    token: string,
    body: any,
    type: ImportType
  ): Promise<any> => {
    setErrors([]);
    setIsLoading(true);
    const headers = getHeaders(token);

    // default import datas --> backend core : import.controller --> importItems()
    let url = `${apiUrl}/api/import/bulk/${type}`;

    // Cas spéciaux pour orders, reception et EDB qui ont leur propre controller au niveau de la gateway
    if (type === ImportType.ORDER || type === ImportType.ORDER_UPDATE) {
      const isUpdate = type === ImportType.ORDER_UPDATE;
      url = `${apiUrl}/api/orders/bulk/${isUpdate ? "update" : "create"}`;
    }
    if (type === ImportType.RECEPTION) {
      url = `${apiUrl}/api/orders/receptions/bulk/create`;
    }
    if (
      type === ImportType.EDB ||
      type === ImportType.EDB_UPDATE ||
      type === ImportType.DOCKYARD_EDB ||
      type === ImportType.DOCKYARD_EDB_UPDATE
    ) {
      const isUpdate =
        type === ImportType.EDB_UPDATE ||
        type === ImportType.DOCKYARD_EDB_UPDATE;
      url = `${apiUrl}/api/edb/bulk/${isUpdate ? "update" : "create"}`;
    }

    const finalBody =
      type === ImportType.EDB ||
      type === ImportType.EDB_UPDATE ||
      type === ImportType.DOCKYARD_EDB ||
      type === ImportType.DOCKYARD_EDB_UPDATE
        ? { ...body, type }
        : body;

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(finalBody),
    });
    setIsLoading(false);
    const data = await response.json();
    if (response.status !== 200) {
      setErrors(data?.errors || []);
    }
    return data;
  };

  const exportData = async (token: string, body: any, url: string) => {
    const headers = getHeaders(token);
    const response = await fetch(`${apiUrl}/api${url}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      return null;
    }
    return response;
  };

  const exportXLSTemplate = async (
    token: string,
    type: string,
    fileName?: string
  ) => {
    const headers = getHeaders(token);
    const response = await fetch(`${apiUrl}/api/import/template`, {
      method: "POST",
      headers,
      body: JSON.stringify({ template_type: type }),
    });

    if (!response.ok) {
      console.error("[exportXLSTemplate] Error | response :", response);
      return null;
    }
    await downloadFile(response, fileName, "xlsx", false);
  };

  const exportXLS = async (
    token: string,
    filters: any,
    type: string,
    fileName?: string
  ) => {
    const response = await exportData(
      token,
      { dataType: type, filters },
      "/export"
    );
    if (response) {
      await downloadFile(response, fileName, "xlsx", true);
    }
  };

  return {
    importItems,
    analyzeImportItems,
    errors,
    setErrors,
    exportData,
    exportXLS,
    exportXLSTemplate,
    isLoading,
    setIsLoading,
  };
};

export default useExport;
